import { computed, watch, nextTick } from 'vue'
import { useTheme as useVuetifyTheme } from 'vuetify'
import { useEditorSettings } from './useEditorSettings'
import {
  EFFECT_THEME_CSS_VAR_KEYS,
  isDarkTheme,
  RAINBOW_CSS_VARS,
  RAINBOW_GRADIENTS,
  THEME_DEFINITIONS,
  THEME_TO_VUETIFY,
} from '../constants/themes'
import type { Theme } from '../types/editor'

const clearCssVars = (keys: readonly string[]) => {
  const root = document.documentElement
  keys.forEach((key) => {
    root.style.removeProperty(key)
  })
}

const syncRainbowGradients = (theme: Theme) => {
  const root = document.documentElement

  if (theme === 'rainbow') {
    Object.entries(RAINBOW_CSS_VARS).forEach(([key, value]) => {
      root.style.setProperty(key, value)
    })
    // Mirror into shared fx vars so floating chrome rules can reuse one token set
    root.style.setProperty('--escribonauta-fx-page', RAINBOW_GRADIENTS.page)
    root.style.setProperty('--escribonauta-fx-surface', RAINBOW_GRADIENTS.surface)
    root.style.setProperty('--escribonauta-fx-surface-variant', RAINBOW_GRADIENTS.surfaceVariant)
    root.style.setProperty('--escribonauta-fx-editor', RAINBOW_GRADIENTS.editor)
    root.style.setProperty('--escribonauta-fx-header', RAINBOW_GRADIENTS.header)
    root.style.setProperty('--escribonauta-fx-title', RAINBOW_GRADIENTS.title)
    return
  }

  Object.keys(RAINBOW_CSS_VARS).forEach((key) => {
    root.style.removeProperty(key)
  })
}

const syncEffectGradients = (theme: Theme) => {
  const root = document.documentElement
  const effectCssVars = THEME_DEFINITIONS[theme]?.effectCssVars

  if (effectCssVars) {
    Object.entries(effectCssVars).forEach(([key, value]) => {
      root.style.setProperty(key, value)
    })
    return
  }

  if (theme !== 'rainbow') {
    clearCssVars(EFFECT_THEME_CSS_VAR_KEYS)
  }
}

export const useTheme = () => {
  const vuetifyTheme = useVuetifyTheme()
  const editorSettings = useEditorSettings()

  const currentTheme = computed(() => editorSettings.theme.value)

  watch(
    () => editorSettings.theme.value,
    async (newTheme) => {
      const vuetifyThemeName = THEME_TO_VUETIFY[newTheme] ?? THEME_TO_VUETIFY.light
      vuetifyTheme.change(vuetifyThemeName)
      await nextTick()
      syncRainbowGradients(newTheme)
      syncEffectGradients(newTheme)
    },
    { immediate: true },
  )

  const toggleTheme = async () => {
    const newTheme = isDark.value ? 'light' : 'dark'
    await editorSettings.updateAndSaveSetting('theme', newTheme)
  }

  const setTheme = async (theme: Theme) => {
    await editorSettings.updateAndSaveSetting('theme', theme)
  }

  const isDark = computed(() => isDarkTheme(currentTheme.value))

  return {
    currentTheme,
    isDark,
    toggleTheme,
    setTheme,
  }
}
